import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type InteresseValue = "topa_freela" | "aberto_mas_ocupado" | "nao_topa";
export type EmpregoValue = "empregado_fixo" | "freela_ativo" | "entre_trabalhos";

export interface AtualizarDisponibilidadeInput {
  interesse?: InteresseValue;
  emprego?: EmpregoValue;
  observacao?: string;
  /** Conversa colada do WhatsApp: se presente, os sinais viram fonte whatsapp. */
  whatsappText?: string;
  /** author_kind: 'humano' na entrada manual; 'ia' quando aplica extração da IA. */
  authorKind?: "humano" | "ia";
  /** Idempotência: hash da conversa (só p/ o fluxo de extração do WhatsApp). */
  dedupKey?: string;
  /**
   * Data em que a informação foi observada ("YYYY-MM-DD"), p/ registro tardio
   * ("falei semana passada"). Vira observed_at/valid_from do sinal e alimenta o
   * frescor. Deve ser passada/hoje (futuro tiraria o sinal do vigente). Default hoje.
   */
  observadoEm?: string;
  /**
   * Data prevista p/ reconsultar / possível disponibilidade ("YYYY-MM-DD", futuro).
   * Vira um availability_checks (reason='data_prevista', status='pendente'). A cada
   * nova reconsulta, as pendentes anteriores do freelancer são canceladas (última vence).
   */
  reconsultarEm?: string;
  /** Origem da sugestão que a pessoa confirmou (ex.: resumo do WhatsApp). */
  sourceRef?: Record<string, unknown>;
}

/** Data de input ("YYYY-MM-DD") -> timestamptz ao MEIO-DIA local, evitando off-by-one de fuso. */
function dateInputToIso(dateStr: string): string {
  return new Date(`${dateStr}T12:00:00`).toISOString();
}

interface SignalRow {
  freelancer_id: string;
  dimension: string;
  value: string;
  source_type: string;
  author_kind: string;
  author_user_id: string | null;
  confidence: string;
  observed_at: string;
  valid_from: string;
  review_status: string;
  observation: string | null;
  quote: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  dedup_key: string | null;
  metadata: Record<string, unknown>;
  source_ref: Record<string, unknown>;
}

/**
 * Grava um ou mais availability_signals (interesse/emprego) a partir de uma edição humana
 * ou de uma extração de WhatsApp aprovada. Reutilizável em qualquer tela (dialog).
 */
export function useAtualizarDisponibilidade(freelancerId?: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: AtualizarDisponibilidadeInput) => {
      if (!freelancerId) throw new Error("Freelancer não informado.");
      const { data: userData } = await supabase.auth.getUser();
      const authorUserId = userData.user?.id ?? null;

      const nowIso = new Date().toISOString();
      // Data da observação: hoje por padrão; se o usuário informar, registra a data real.
      const observedIso = input.observadoEm ? dateInputToIso(input.observadoEm) : nowIso;
      const fromWhatsapp = !!input.whatsappText?.trim();
      const sourceType = fromWhatsapp ? "whatsapp_ia" : "manual_humano";
      const authorKind = input.authorKind ?? "humano";
      const quote = input.whatsappText?.trim() || null;
      const observation = input.observacao?.trim() || null;

      const axes: { dimension: string; value: string }[] = [];
      if (input.interesse) axes.push({ dimension: "interesse_freela", value: input.interesse });
      if (input.emprego) axes.push({ dimension: "situacao_emprego", value: input.emprego });
      if (!axes.length) throw new Error("Informe ao menos o interesse ou a situação de emprego.");

      const rows: SignalRow[] = axes.map((axis, index) => ({
        freelancer_id: freelancerId,
        dimension: axis.dimension,
        value: axis.value,
        source_type: sourceType,
        author_kind: authorKind,
        author_user_id: authorUserId,
        confidence: "alta",
        observed_at: observedIso,
        valid_from: observedIso,
        // Humano aprovou (manual ou revisão da IA) -> trava acima da IA automática.
        review_status: "confirmado_humano",
        observation,
        quote,
        reviewed_by: authorKind === "ia" ? authorUserId : null,
        reviewed_at: authorKind === "ia" ? nowIso : null,
        // dedup_key só no primeiro sinal do lote evita colisão no índice único parcial.
        dedup_key: input.dedupKey ? `${input.dedupKey}:${axis.dimension}` : null,
        metadata: {
          origem: fromWhatsapp
            ? "whatsapp_paste"
            : input.sourceRef
              ? "whatsapp_resumo"
              : "dialog_manual",
        },
        source_ref: input.sourceRef ?? {},
      }));

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: inserted, error } = await (supabase as any)
        .from("availability_signals")
        .upsert(rows, { onConflict: "dedup_key", ignoreDuplicates: true })
        .select("id");
      // O client do Supabase resolve (não rejeita) com um objeto plano em `error`
      // ({message, details, hint, code}), não uma instância de Error — precisa
      // virar Error de verdade pra quem consome (toast, Sentry) mostrar a mensagem.
      if (error) throw new Error(error.message || "Falha ao salvar disponibilidade.");

      // Data prevista de reconsulta -> availability_checks (lembrete de re-consulta).
      if (input.reconsultarEm) {
        // Última reconsulta vence: cancela as pendentes anteriores deste freelancer.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error: cancelError } = await (supabase as any)
          .from("availability_checks")
          .update({ status: "cancelado", resolved_at: nowIso })
          .eq("freelancer_id", freelancerId)
          .eq("reason", "data_prevista")
          .eq("status", "pendente");
        if (cancelError) throw new Error(cancelError.message || "Falha ao cancelar reconsultas anteriores.");

        const signalId = (inserted as { id: string }[] | null)?.[0]?.id ?? null;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error: checkError } = await (supabase as any)
          .from("availability_checks")
          .insert({
            freelancer_id: freelancerId,
            due_at: dateInputToIso(input.reconsultarEm),
            reason: "data_prevista",
            channel: "manual",
            status: "pendente",
            signal_id: signalId,
            note: observation,
            created_by: authorUserId,
          });
        if (checkError) throw new Error(checkError.message || "Falha ao registrar reconsulta.");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["disponibilidade"] });
      queryClient.invalidateQueries({ queryKey: ["reconsulta"] });
    },
  });
}
